import React from "react";
import { Box, Button, ListItem, List, ListItemText } from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useStyles } from "../style";
import { channelPostDetails, getChannelActivePost } from "../../../../redux/actions/programAction";
import DangerAlertIcon from "../../../../components/Icons/DangerAlertIcon";
import LaunchIcon from "../../../../components/Icons/LaunchIcon";
import { getSecondaryPostListContent } from "../../../../util/lib";
import toastr from "toastr";
import { handleDrawer } from "../../../../redux/actions/drawerAction";
import { useIsMobile } from "../../../../util/hooks";
import { RootState } from "../../../../redux/store";
import { DEFAULT_CHANNEL_THEME, sanitizeChannelTheme } from "../../../../util/theme";

/* ---- helpers ---- */
const hexToRgb = (hex: string) => {
  let c = (hex || "#000000").replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const n = parseInt(c, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const alpha = (hex: string, a: number) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(a, 0), 1)})`;
};
/** pick black/white with good contrast on the given background */
const pickTextOn = (bgHex: string) => {
  let c = (bgHex || "#000000").replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const n = parseInt(c, 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const sr = r / 255, sg = g / 255, sb = b / 255;
  const rl = sr <= 0.03928 ? sr / 12.92 : Math.pow((sr + 0.055) / 1.055, 2.4);
  const gl = sg <= 0.03928 ? sg / 12.92 : Math.pow((sg + 0.055) / 1.055, 2.4);
  const bl = sb <= 0.03928 ? sb / 12.92 : Math.pow((sb + 0.055) / 1.055, 2.4);
  const L = 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
  return L > 0.5 ? "#121212" : "#ffffff";
};

/* --- new: parse CSS rgb/rgba and blend to find the REAL pill background --- */
const cssToRgb = (css: string | null): { r: number; g: number; b: number } | null => {
  if (!css) return null;
  const m = css.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!m) return null;
  return { r: +m[1], g: +m[2], b: +m[3] };
};
const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }) =>
  `#${[r, g, b].map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0")).join("")}`;
const blendOver = (under: { r: number; g: number; b: number }, over: { r: number; g: number; b: number }, a: number) => ({
  r: Math.round(over.r * a + under.r * (1 - a)),
  g: Math.round(over.g * a + under.g * (1 - a)),
  b: Math.round(over.b * a + under.b * (1 - a)),
});

const PostList = ({ item }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const isMobile = useIsMobile();

  // THEME (channel → community → defaults)
  const { singleProgram } = useSelector((s: RootState) => s.programReducer);
  const channelThemeReducer = useSelector((s: RootState) => (s as any).channelThemeReducer || {});
  const { communityIdentifier } = JSON.parse(localStorage.getItem("activeProgramEnrollment") || "{}");
  const chTheme = channelThemeReducer[item.channelIdentifier] || {};
  const community = singleProgram?.communities?.find((c: any) => c?.communityIdentifier === communityIdentifier);
  const cStyle = community?.communityStyle;
  const resolvedTheme = React.useMemo(
    () => sanitizeChannelTheme(DEFAULT_CHANNEL_THEME, cStyle, chTheme),
    [cStyle, chTheme]
  );
  const mainColor = resolvedTheme.mainColor;
  const secondaryColor = resolvedTheme.secondaryColor;
  const fontStyle = resolvedTheme.fontStyle;

  // Base (initial) guess
  const fg   = pickTextOn(secondaryColor);
  const fg80 = alpha(fg, 0.8);

  // Soft pill using secondary color
  const pillAlpha = 0.10;
  const pillBg   = alpha(secondaryColor, pillAlpha);
  const pillBord = alpha(secondaryColor, 0.35);
  const acc80    = alpha(mainColor, 0.80);

  // NEW: compute effective background of the rows and choose text against that
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const [fgVis, setFgVis] = React.useState(fg);
  const [fg80Vis, setFg80Vis] = React.useState(fg80);

  React.useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    // find the first ancestor with a non-transparent background (fallback to white)
    let cursor: HTMLElement | null = el;
    let under: { r: number; g: number; b: number } | null = null;
    while (cursor && !under) {
      const bg = getComputedStyle(cursor).backgroundColor;
      const rgb = cssToRgb(bg);
      if (rgb && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
        under = rgb;
        break;
      }
      cursor = cursor.parentElement as HTMLElement | null;
    }
    if (!under) under = { r: 255, g: 255, b: 255 }; // default to white page bg
    const over = hexToRgb(secondaryColor);
    const mixed = blendOver(under, over, pillAlpha);
    const effectiveBgHex = rgbToHex(mixed);
    const c = pickTextOn(effectiveBgHex);
    setFgVis(c);
    setFg80Vis(alpha(c, 0.8));
  }, [secondaryColor, pillAlpha]);

  const scopeId = React.useMemo(
    () => `pl-scope-${item.channelIdentifier || "x"}`,
    [item.channelIdentifier]
  );

  const posts = item[item.channelIdentifier] || [];

  const isActive = (nestedItem: any) => {
    const search = new URLSearchParams({
      contextId: nestedItem.contextId,
      channelIdentifier: item.channelIdentifier,
    }).toString();
    return location.pathname === "/user/post" && location.search === `?${search}`;
  };

  const loadPost = async (nestedItem: any) => {
    if (isMobile) dispatch(handleDrawer(false));
    const details = await dispatch(
      channelPostDetails(nestedItem.programId, nestedItem.userId, nestedItem.contextId)
    );
    if (!details) { toastr.error("Unable to fetch post details"); return; }
    if ((nestedItem as any).isContextArchived) {
      (details as any).isContextArchived = (nestedItem as any).isContextArchived;
    }
    Object.assign(details, {
      channelPostingSettings: item.channelPostingSettings,
      channelPrivacySettings: item.channelPrivacySettings,
      channelFormSettings: item.channelFormSettings,
      channelIdentifier: item.channelIdentifier,
    });
    dispatch(getChannelActivePost(details));
    const search = new URLSearchParams({
      contextId: nestedItem.contextId,
      channelIdentifier: item.channelIdentifier,
    }).toString();
    history.push(`/user/post?${search}`);
  };

  return (
    <>
      <style>{`
        [data-pl-scope="${scopeId}"] .pl-item {
          background: ${pillBg} !important;
          border: 1px solid ${pillBord} !important;
          border-radius: 12px !important;
          color: ${fgVis} !important;
          font-family: ${fontStyle}, sans-serif !important;
        }
        [data-pl-scope="${scopeId}"] .pl-item:hover {
          border-color: ${acc80} !important;
        }
        /* keep wrappers transparent */
        [data-pl-scope="${scopeId}"] .pl-item .${classes.listItemWrapper},
        [data-pl-scope="${scopeId}"] .pl-item .${classes.textWrapper} {
          background: transparent !important;
        }
        /* paint MUI typography nodes (force win) */
        [data-pl-scope="${scopeId}"] .pl-item .MuiListItemText-primary {
          color: ${fgVis} !important;
          -webkit-text-fill-color: ${fgVis} !important;
          text-shadow: 0 0 0 ${fgVis};
          font-weight: 600;
          line-height: 1.35;
        }
        [data-pl-scope="${scopeId}"] .pl-item .MuiListItemText-secondary {
          color: ${fg80Vis} !important;
          -webkit-text-fill-color: ${fg80Vis} !important;
          text-shadow: 0 0 0 ${fg80Vis};
          line-height: 1.3;
        }
      `}</style>

      <List
        
        disablePadding
        className={classes.sideBarCollapse}
        data-pl-scope={scopeId}
        ref={listRef}
        style={{
          fontFamily: `${fontStyle}, sans-serif`,
          color: fgVis,
          WebkitTextFillColor: fgVis as any,
          filter: "none",
          mixBlendMode: "normal",
          opacity: 1,
        }}
      >
        {posts.map((nestedItem: any, idx: number) => {
          const active = isActive(nestedItem);
          const title = nestedItem?.data?.title || nestedItem?.data?.heading || "";
          const sub   = getSecondaryPostListContent(nestedItem?.data) || "";

          return (
            <ListItem
              key={idx}
              button
              disableRipple
              disableTouchRipple
              selected={active}
              className={`pl-item ${classes.nested}`}
              onClick={() => loadPost(nestedItem)}
            >
              <div
                className={classes.listItemWrapper}
                style={{ width: "100%", display: "flex", alignItems: "center" }}
              >
                {/* text block: prevent overflow */}
                <div
                  className={classes.textWrapper}
                  style={{ position: "relative", zIndex: 2, flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}
                >
                  <ListItemText
                    primary={title || "—"}
                    secondary={sub || " "}
                    primaryTypographyProps={{
                      noWrap: true,
                      style: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
                    }}
                    secondaryTypographyProps={{
                      noWrap: true,
                      style: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
                    }}
                  />
                </div>

                {nestedItem.alert && (
                  <div className={classes.hasNotification} style={{ color: mainColor, zIndex: 2, marginLeft: 8 }}>
                    <DangerAlertIcon />
                  </div>
                )}

                <div className={classes.divider} />

                <a
                  href={nestedItem.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: pickTextOn(mainColor), position: "relative", zIndex: 2, marginLeft: 8, flex: "0 0 auto" }}
                >
                  <LaunchIcon />
                </a>
              </div>
            </ListItem>
          );
        })}

        {posts.length > 0 && (
          <Box display="flex" justifyContent="center" m={1}>
            <Button
              variant="contained"
              className={classes.viewMorebtn}
              onClick={() => history.push("/user/channel")}
              style={{
                background: mainColor,
                color: pickTextOn(mainColor) as any,
                WebkitTextFillColor: pickTextOn(mainColor) as any,
              }}
            >
              View More
            </Button>
          </Box>
        )}
      </List>
    </>
  );
};

export default PostList;
